import axios from 'axios';

import { computeLogoEditWindow } from '../../../casino-settings/logos';
import { mergePlayers, type RawRow } from '../../../players/roster-merge';
import type { LogoLock } from './logos-summary';

/**
 * The Logos card's lock: the Logos page's verdict (`computeLogoEditWindow` over the player roster, or unknown when the
 * registration list could not be read, the page's `unknownLogoEditWindow`), without paging the whole roster in.
 *
 * The page counts the roster `usePlayersRoster` builds: the Stats `players` registration list (`fetchAllPlayers`)
 * merged with the activity data set. This reads the same registration list, with the same request, at most two pages,
 * and stops as soon as the verdict is known: past the cap after any page, or at the list's last (short) page. Two full
 * pages still within the cap (repeated emails, or rows with neither an email nor a player id) leave it unknown. Players
 * are counted as the merge counts them (one per email, or per player id without one). The activity data set is not
 * read: on a consistent backend every player in it is a registered one, so it never adds to the count (the roster
 * unions it in for a registration list cut off at its 2000-row cap, which is far past the 50 here).
 */

/** The registration list's page (as `fetchAllPlayers` in `ownerHubStats` pages it), and the spec's cap on this read. */
const PAGE = 50;
const MAX_PAGES = 2;
const ORDER = { date_created_tz: 'desc' };

/** A `players` row, as `fetchAllPlayers` keeps one: anything with a string `email` or `player_id`. */
const isPlayerRow = (value: unknown): value is RawRow =>
  typeof value === 'object' &&
  value !== null &&
  (typeof (value as { email?: unknown }).email === 'string' ||
    typeof (value as { player_id?: unknown }).player_id === 'string');

/** The lock the registration rows read so far decide (the roster's player count), as the Logos page rules it. */
export const lockFor = (registrations: readonly RawRow[]): 'editable' | 'locked' =>
  computeLogoEditWindow(mergePlayers(registrations, []).length).editable ? 'editable' : 'locked';

const statsBase = (): string => {
  const value = (window.config as unknown as Record<string, unknown> | undefined)?.ownerHubStatsApiUrl;
  return typeof value === 'string' ? value : '';
};

/**
 * What one read of the lock tells: the page's verdict, or `capped`, every page read full and still within the cap
 * (repeated emails, or rows with neither an email nor a player id), the rest of the list able to pass it: only the
 * whole list decides then.
 */
export type LogoLockRead = Exclude<LogoLock, 'pending'> | 'capped';

/**
 * The lock verdict for one brand, telling a capped read apart (LogoLockRead). A read the roster would fail (no token,
 * an error payload on the first page, any request error) is the page's unknown-count verdict, `unknown`; an error
 * payload on a later page keeps the rows read so far, as the roster does.
 */
export const readLogoLockDetail = async (
  casinoInstanceId: string,
  token: string | null | undefined,
  signal: AbortSignal,
): Promise<LogoLockRead> => {
  const base = statsBase();
  if (!token || !base) return 'unknown';
  const rows: RawRow[] = [];
  try {
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const body = {
        casino_instance_id: casinoInstanceId,
        data_set: 'players',
        order_by: ORDER,
        limit: PAGE,
        offset: page * PAGE,
      };
      // eslint-disable-next-line no-await-in-loop -- offset paging is sequential, and each page decides the next.
      const response = await axios.post(`${base}/select/data-set`, body, { headers: { Authorization: token }, signal });
      if (!Array.isArray(response.data)) return page === 0 ? 'unknown' : lockFor(rows);
      rows.push(...(response.data as unknown[]).filter(isPlayerRow));
      // Past the cap, no later page can reopen the lock.
      if (lockFor(rows) === 'locked') return 'locked';
      // The list's last (short) page: the count is whole.
      if (response.data.length < PAGE) return lockFor(rows);
    }
  } catch {
    return 'unknown';
  }
  // Every page read was full and still within the cap (repeated emails, or rows with neither an email nor a player
  // id): the rest of the list could pass it.
  return 'capped';
};

/** The lock verdict for one brand, as the hub's Logos card shows it: a capped read is unknown there. */
export const readLogoLock = async (
  casinoInstanceId: string,
  token: string | null | undefined,
  signal: AbortSignal,
): Promise<Exclude<LogoLock, 'pending'>> => {
  const read = await readLogoLockDetail(casinoInstanceId, token, signal);
  return read === 'capped' ? 'unknown' : read;
};
